import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import FixedLines from '../components/common/FixedLines';
import UiIcon from '../components/common/UiIcon';
import { SITE_CONFIG } from '../config/site';
import { getSpreadChuruCost } from '../lib/walletFlow';
import type { SpreadType } from '../lib/types';
import { trackEvent } from '../lib/analytics';

interface SpreadOption {
  id: SpreadType;
  title: string;
  subtitle: string;
  descriptionLines: string[];
  readingStyle: string;
  bestForLines: string[];
  quickGuideLines: string[];
  guideLabel: string;
  guideLines: string[];
  recommended?: boolean;
  cardCount: number;
  icon: 'one-card' | 'three-card' | 'cross-spread';
  bgGradient: string;
  borderColor: string;
  iconColor: string;
}

const spreadIntroLines = [
  '오늘 질문은 짧게 볼지, 흐름으로 볼지,',
  '깊게 펼쳐볼지 먼저 정해 주세요.',
  '가벼운 확인도 좋고, 오래 걸린 고민도 괜찮습니다.',
  '지금 마음에 맞는 판을 고르면 됩니다.',
];

const spreads: SpreadOption[] = [
  {
    id: 'one-card',
    title: '원카드',
    subtitle: 'ONE CARD',
    descriptionLines: ['지금 제일 먼저 봐야 할 답만', '한 장으로 또렷하게 짚습니다.'],
    readingStyle: '짧게, 바로 핵심만',
    bestForLines: ['오래 설명 듣기보다 바로 확인하고 싶을 때', '이미 마음은 아는데 한 번 더 짚고 싶을 때'],
    quickGuideLines: ['오늘 바로 붙잡을', '한마디가 필요할 때'],
    guideLabel: '고를 때 참고',
    guideLines: ['생각이 너무 많을수록', '처음부터 깊게 파기보다', '한 장으로 방향부터 보는 편이 낫습니다.'],
    cardCount: 1,
    icon: 'one-card',
    bgGradient: 'from-[#2d1e18] to-[#1a1614]',
    borderColor: 'border-gold/30',
    iconColor: 'text-gold/80',
  },
  {
    id: 'three-card',
    title: '쓰리카드',
    subtitle: 'THREE CARD',
    descriptionLines: ['어디서 시작됐고, 지금 무엇이 핵심인지', '세 장으로 흐름을 따라갑니다.'],
    readingStyle: '흐름을 읽는 이야기형',
    bestForLines: ['왜 여기까지 왔는지 보고 싶을 때', '다음 장면까지 같이 짚고 싶을 때'],
    quickGuideLines: ['처음이라면 가장 무난하게', '고르기 좋은 세션'],
    guideLabel: '처음이면 추천',
    guideLines: ['처음이라면 쓰리카드가 편합니다.', '너무 무겁지 않게, 그래도 흐름은 놓치지 않습니다.'],
    recommended: true,
    cardCount: 3,
    icon: 'three-card',
    bgGradient: 'from-[#3a1a1a] to-[#2a1414]',
    borderColor: 'border-primary/40',
    iconColor: 'text-primary',
  },
  {
    id: 'celtic-cross',
    title: '켈틱 크로스',
    subtitle: 'CELTIC CROSS',
    descriptionLines: ['지금 상황, 속마음, 주변 변수까지', '한 판 안에서 길게 펼쳐봅니다.'],
    readingStyle: '깊게 들어가는 정식 세션',
    bestForLines: ['단순한 답보다 구조가 궁금할 때', '한 번에 판 전체를 보고 싶을 때'],
    quickGuideLines: ['질문이 오래 묵었고', '전체 그림을 보고 싶을 때'],
    guideLabel: '깊게 읽고 싶다면',
    guideLines: ['가볍게 넘기기 어려운 질문이라면', '켈틱 크로스가 맞습니다.', '대신 조금 길게 읽습니다.'],
    cardCount: 10,
    icon: 'cross-spread',
    bgGradient: 'from-[#211813] to-[#15100d]',
    borderColor: 'border-[#8a6a4b]/45',
    iconColor: 'text-[#c79b66]',
  },
];

export default function SpreadSelectionPage() {
  const navigate = useNavigate();
  const [hoveredSpread, setHoveredSpread] = useState<SpreadType | null>(null);
  const [hasStartedReading, setHasStartedReading] = useState(false);

  useEffect(() => {
    setHasStartedReading(localStorage.getItem('myoan_has_started_reading') === '1');
  }, []);

  const handleSpreadSelect = (spreadId: SpreadType) => {
    trackEvent('spread_selected', {
      spread: spreadId,
      churu_cost: getSpreadChuruCost(spreadId),
    });
    sessionStorage.setItem('selected_spread', spreadId);
    localStorage.setItem('myoan_has_started_reading', '1');
    setHasStartedReading(true);
    navigate(`/themes/${spreadId}`);
  };

  return (
    <div className="relative flex min-h-screen flex-col overflow-x-hidden bg-background-dark font-display text-white selection:bg-primary selection:text-white">
      <div className="pointer-events-none fixed inset-0 z-50 bg-grain opacity-[0.07]" />

      <header className="absolute left-0 top-0 z-40 flex w-full items-center p-8 opacity-70 transition-opacity duration-300 hover:opacity-100">
        <Link
          to="/"
          aria-label="메인 페이지로 이동"
          className="flex items-center transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-gold/60 focus:ring-offset-2 focus:ring-offset-background-dark"
        >
          <span className="text-sm font-bold uppercase tracking-[0.3em] text-parchment">
            {SITE_CONFIG.serviceNameEn}
          </span>
        </Link>
      </header>

      <main className="relative z-10 flex flex-grow flex-col items-center justify-center px-6 pb-16 pt-32">
        <div className="mb-16 text-center">
          <h1 className="mb-4 font-serif text-4xl font-extrabold leading-tight tracking-[-0.02em] text-[#f5efe7] md:text-5xl lg:text-6xl">
            스프레드를 선택하세요
          </h1>
          <FixedLines
            lines={spreadIntroLines}
            as="p"
            className="mx-auto mt-4 max-w-2xl text-sm font-light tracking-wide text-parchment/70 md:text-base"
            lineClassName="md:whitespace-nowrap"
          />
        </div>

        <div className="mx-auto w-full max-w-6xl">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-10">
            {spreads.map((spread) => {
              const isHovered = hoveredSpread === spread.id;
              const spreadCost = getSpreadChuruCost(spread.id);

              return (
                <div
                  key={spread.id}
                  className="spread-card-container perspective-1000 flex flex-col items-center"
                  onMouseEnter={() => setHoveredSpread(spread.id)}
                  onMouseLeave={() => setHoveredSpread(null)}
                  onClick={() => handleSpreadSelect(spread.id)}
                >
                  <div className="mb-5 flex min-h-[3.5rem] items-end justify-center">
                    <span className="font-serif text-[2rem] font-extrabold leading-none tracking-[-0.02em] text-[#f6c558] drop-shadow-[0_8px_20px_rgba(0,0,0,0.45)]">
                      {spreadCost}츄르
                    </span>
                  </div>

                  <div
                    className={`spread-card relative w-full cursor-pointer rounded-xl border bg-gradient-to-br p-8 transition-all duration-300 ${
                      spread.bgGradient
                    } ${spread.borderColor} ${
                      isHovered
                        ? 'translate-y-[-0.35rem] border-[#c79b66]/70'
                        : ''
                    }`}
                  >
                    {spread.recommended && !hasStartedReading && (
                      <div className="absolute right-5 top-5 rounded-full border border-[#6c4416]/70 bg-[#18120f]/96 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-[#e0b86f] shadow-[0_10px_24px_-18px_rgba(0,0,0,0.8)]">
                        처음이면 추천
                      </div>
                    )}

                    <div className="mb-6 flex justify-center">
                      <div
                        className={`relative ${spread.iconColor}`}
                      >
                        <UiIcon name={spread.icon} className="h-20 w-20" />
                      </div>
                    </div>

                    <h2 className="mb-2 text-center text-2xl font-bold text-white">{spread.title}</h2>
                    <p className={`mb-4 text-center text-sm font-bold uppercase tracking-widest ${spread.iconColor}`}>
                      {spread.subtitle}
                    </p>
                    <div
                      className={`mx-auto mb-6 h-[1px] w-16 bg-gradient-to-r from-transparent via-current to-transparent ${spread.iconColor}`}
                    />

                    <FixedLines
                      lines={spread.quickGuideLines}
                      as="p"
                      className="mb-3 min-h-[2rem] text-center text-[11px] uppercase tracking-[0.16em] text-parchment/75"
                    />

                    <FixedLines
                      lines={spread.descriptionLines}
                      as="p"
                      className="mb-3 min-h-[3.5rem] text-center text-sm leading-relaxed text-parchment/80"
                    />

                    <div className="mb-4 border border-[#fbbf24]/10 bg-black/20 px-4 py-3 text-left">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-parchment/45">묘안이 읽는 방식</p>
                      <p className="mt-2 text-sm text-white/90">{spread.readingStyle}</p>
                      <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-parchment/45">이럴 때 맞아요</p>
                      <FixedLines lines={spread.bestForLines} as="p" className="mt-2 text-xs leading-6 text-parchment/70" />
                    </div>

                    <div className="mb-10 border border-[#fbbf24]/10 bg-black/20 px-4 py-3 text-left">
                      <p
                        className={`text-[10px] uppercase tracking-[0.22em] ${
                          spread.recommended ? 'text-[#fbbf24]/65' : 'text-[#fbbf24]/50'
                        }`}
                      >
                        {spread.guideLabel}
                      </p>
                      <FixedLines lines={spread.guideLines} as="p" className="mt-2 text-sm leading-6 text-[#f6ead8]" />
                    </div>

                    <div className="flex justify-center">
                      <div className={`inline-flex items-center gap-2 rounded-full border bg-black/30 px-4 py-2 ${spread.borderColor}`}>
                        <UiIcon name="book" className="h-4 w-4 text-parchment/60" />
                        <span className="text-xs uppercase tracking-widest text-parchment/80">{spread.cardCount}장 리딩</span>
                      </div>
                    </div>

                    {isHovered && (
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#c79b66]">
                          <UiIcon name="arrow-right" className="h-4 w-4" />
                          <span>질문 고르기</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-16 text-center">
          <p className="text-xs italic tracking-[0.2em] text-parchment/40">
            판을 고르면, 그 판에 어울리는 질문으로 이어집니다.
          </p>
        </div>
      </main>
      <style>{`
        .perspective-1000 {
          perspective: 1000px;
        }
      `}</style>
    </div>
  );
}
